import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://ijqjbawxcqsyotuvqtqx.supabase.co'
const supabaseAnonKey =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlqcWpiYXd4Y3FzeW90dXZxdHF4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgwODU5NzcsImV4cCI6MjA4MzY2MTk3N30.4xGgSN__AeIBLQ2koU7KxrW7WXhVkelU8Jp_84Pr--4'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Storage bucket name
export const STORAGE_BUCKET = 'project-images'

// Helper to upload image to Supabase Storage
export async function uploadImage(
  file: File | Blob,
  path: string,
): Promise<{ url: string | null; error: any }> {
  try {
    console.log('Uploading to:', path)
    console.log('File size:', file.size, 'bytes')

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true,
      })

    if (error) {
      console.error('Upload error:', error)

      // Provide helpful error messages
      if (error.message.includes('not found')) {
        return {
          url: null,
          error: new Error(
            'Storage bucket "project-images" not found. Please create it in Supabase Dashboard → Storage.',
          ),
        }
      }

      if (error.message.includes('policy')) {
        return {
          url: null,
          error: new Error(
            'Storage permissions error. Please set up storage policies (see SUPABASE_STORAGE_SETUP.md).',
          ),
        }
      }

      throw error
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(data.path)

    console.log('Upload successful:', publicUrl)
    return { url: publicUrl, error: null }
  } catch (error: any) {
    console.error('Upload error:', error)
    return {
      url: null,
      error: error.message || 'Unknown upload error',
    }
  }
}

// Helper to convert base64 to Blob
export function base64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,')
  const contentType = parts[0].split(':')[1]
  const raw = window.atob(parts[1])
  const rawLength = raw.length
  const uInt8Array = new Uint8Array(rawLength)

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i)
  }

  return new Blob([uInt8Array], { type: contentType })
}

// Helper to get file extension from base64
export function getFileExtension(base64: string): string {
  const match = base64.match(/data:image\/(\w+);/)
  return match ? match[1] : 'jpg'
}

// Database types - UPDATED to use Storage URLs
export interface Project {
  id: string
  title: string
  category: string[]
  year: string
  image_path: string // Storage path instead of base64
  thumb_path?: string // Storage path for thumbnail
  media_type?: 'image' | 'video'
  description?: string
  position: number
  created_at?: string
}

// Lightweight version for list queries (no image data)
export interface ProjectListItem {
  id: string
  title: string
  category: string[]
  year: string
  image_path: string // Added for fallback
  thumb_path?: string // Only thumbnail path for grid
  media_type?: 'image' | 'video'
  position: number
}

export interface ProjectsPageItem {
  id: string
  title: string
  description: string
  image_path: string // Storage path instead of base64
  link?: string
  category?: string
  button_text?: string
  button_disabled?: boolean
  position: number
  created_at?: string
}

// Helper to get public URL from storage path
export function getImageUrl(path: string | null | undefined): string | null {
  if (!path) {
    console.warn('getImageUrl called with null/undefined path')
    return null
  }

  // If it's already a full URL, return it
  if (path.startsWith('http')) {
    console.log('Path is already a URL:', path)
    return path
  }

  // If it's a base64 string (legacy), return it as-is
  if (path.startsWith('data:')) {
    console.log('Path is base64, returning as-is')
    return path
  }

  // Otherwise, construct Storage URL
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path)
  console.log('Constructed Storage URL:', data.publicUrl, 'from path:', path)
  return data.publicUrl
}
